import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  arrayUnion,
  getDocs,
} from 'firebase/firestore';
import TripSession from '../components/TripSession';
import './RoadTrip.css';
import { functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';

// Generate a 6-character trip code
const generateTripCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

function RoadTrip({ user }) {
  const [view, setView] = useState('home');
  const [tripName, setTripName] = useState('');
  const [tripPurpose, setTripPurpose] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [activeTrip, setActiveTrip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [myTrips, setMyTrips] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [showParticipantSelector, setShowParticipantSelector] = useState(false);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [gamesEnabled, setGamesEnabled] = useState(false); // Games disabled by default

  // Check for active trips on mount
  useEffect(() => {
    if (!user) return;

    const tripsRef = collection(db, 'roadTrips');
    const q = query(
      tripsRef,
      where('participantIds', 'array-contains', user.uid),
      where('status', 'in', ['waiting', 'active'])
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const trips = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMyTrips(trips);

      const active = trips.find(
        (t) => t.status === 'active' || t.status === 'waiting'
      );
      if (active && !activeTrip) {
        setActiveTrip(active);
        setView('active');
      }
    });

    return () => unsubscribe();
  }, [user, activeTrip]);

  // Load available users for participant selection
  useEffect(() => {
    if (!user || view !== 'create') return;

    const loadUsers = async () => {
      try {
        // First, try to get users from Cloud Function (gets all Firebase Auth users)
        try {
          const getUsersFunction = httpsCallable(functions, 'getUsers');
          const result = await getUsersFunction();

          if (result.data.success && result.data.users) {
            const users = result.data.users
              .filter((u) => u.uid !== user.uid && u.email) // Exclude current user
              .map((u) => ({
                uid: u.uid,
                email: u.email,
                name: u.name || u.email.split('@')[0],
                avatar: u.avatar || null,
              }));

            if (users.length > 0) {
              setAvailableUsers(users);
              return;
            }
          }
        } catch (funcError) {
          console.log(
            'Cloud Function not available, trying Firestore...',
            funcError
          );
        }

        // Fallback: Try to get users from Firestore users collection
        const usersRef = collection(db, 'users');
        const snapshot = await getDocs(usersRef);

        if (snapshot.size > 0) {
          const users = snapshot.docs
            .map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }))
            .filter((u) => u.id !== user.uid && u.email) // Exclude current user
            .map((u) => ({
              uid: u.id,
              email: u.email,
              name: u.displayName || u.name || u.email.split('@')[0],
              avatar: u.photoURL || null,
            }));

          if (users.length > 0) {
            setAvailableUsers(users);
            return;
          }
        }

        // If no users found, show empty list
        setAvailableUsers([]);
        console.log(
          'No other users found. Users can still join with trip code.'
        );
      } catch (err) {
        console.log('Could not load users:', err);
        setAvailableUsers([]);
      }
    };

    loadUsers();
  }, [user, view]);

  // Reset selected participants when leaving create view
  useEffect(() => {
    if (view !== 'create') {
      setSelectedParticipants([]);
      setShowParticipantSelector(false);
    }
  }, [view]);

  const handleCreateTrip = async () => {
    if (!tripName.trim()) {
      setError('Please enter a trip name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const code = generateTripCode();
      const userName = user.displayName || user.email.split('@')[0];

      const tripData = {
        name: tripName.trim(),
        purpose: tripPurpose.trim(),
        code: code,
        hostId: user.uid,
        hostName: userName,
        status: 'waiting',
        participants: [
          {
            uid: user.uid,
            name: userName,
            email: user.email,
            isHost: true,
            score: 0,
            joinedAt: new Date().toISOString(),
          },
        ],
        participantIds: [user.uid],
        currentTrackIndex: 0,
        gamesEnabled: gamesEnabled, // Use toggle state
        isPlaying: false, // Playback state synced across all participants
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, 'roadTrips'), tripData);
      const createdTrip = { id: docRef.id, ...tripData };

      // Automatically create a playlist for this Road Trip
      let playlistId = null;
      try {
        const playlistDoc = {
          name: tripName.trim(),
          description: tripPurpose.trim()
            ? `Road Trip: ${tripPurpose.trim()}`
            : `Road Trip: ${tripName.trim()}`,
          userId: user.uid,
          userEmail: user.email,
          tracks: [], // Start with empty tracks, will be updated as songs are added
          trackCount: 0,
          source: 'roadTrip',
          roadTripId: docRef.id,
          roadTripName: tripName.trim(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        const playlistRef = await addDoc(
          collection(db, 'playlists'),
          playlistDoc
        );
        playlistId = playlistRef.id;

        // Update the trip with the playlist ID
        await updateDoc(docRef, {
          playlistId: playlistRef.id,
        });

        console.log('✅ Auto-created playlist for Road Trip:', playlistRef.id);
      } catch (playlistErr) {
        console.error(
          '⚠️ Failed to auto-create playlist (trip still created):',
          playlistErr
        );
        // Don't fail trip creation if playlist creation fails
      }

      setActiveTrip({ ...createdTrip, playlistId });

      // Send email invitations to selected participants
      if (selectedParticipants.length > 0) {
        setSendingInvites(true);
        await sendInvitations(createdTrip, selectedParticipants, userName);
        setSendingInvites(false);
      }

      setView('active');
    } catch (err) {
      console.error('Error creating trip:', err);
      // Show more detailed error message
      const errorMessage = err.message || err.code || 'Unknown error';
      setError(
        `Failed to create trip: ${errorMessage}. Please check the browser console for details.`
      );
    } finally {
      setLoading(false);
      setSendingInvites(false);
    }
  };

  // Send email invitations to selected participants via Cloud Function
  const sendInvitations = async (trip, participants, hostName) => {
    try {
      const sendInvites = httpsCallable(functions, 'sendRoadTripInvites');

      const result = await sendInvites({
        trip: {
          name: trip.name,
          purpose: trip.purpose,
          code: trip.code,
        },
        participants: participants.map((p) => ({
          uid: p.uid,
          email: p.email,
          name: p.name,
        })),
        hostName: hostName,
      });

      console.log('📧 Invitation results:', result.data);

      if (result.data.success) {
        const sent = result.data.sent || 0;
        const failed = result.data.failed || 0;
        if (sent > 0) {
          console.log(`✅ Sent ${sent} invitation(s)`);
        }
        if (failed > 0) {
          console.warn(`⚠️  ${failed} invitation(s) failed`);
        }
      }
    } catch (emailErr) {
      console.error('Failed to send invitations:', emailErr);
      // Don't fail the whole trip creation if email fails
      // User can still share code manually
    }
  };

  const handleJoinTrip = async () => {
    const code = joinCode.trim().toUpperCase();

    if (code.length !== 6) {
      setError('Please enter a valid 6-character code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const tripsRef = collection(db, 'roadTrips');
      const q = query(tripsRef, where('code', '==', code));

      const snapshot = await new Promise((resolve) => {
        const unsubscribe = onSnapshot(q, (snap) => {
          unsubscribe();
          resolve(snap);
        });
      });

      if (snapshot.empty) {
        setError('Trip not found. Check the code and try again.');
        setLoading(false);
        return;
      }

      const tripDoc = snapshot.docs[0];
      const tripData = tripDoc.data();

      if (tripData.status === 'ended') {
        setError('This trip has already ended.');
        setLoading(false);
        return;
      }

      if (tripData.participantIds.includes(user.uid)) {
        setActiveTrip({ id: tripDoc.id, ...tripData });
        setView('active');
        setLoading(false);
        return;
      }

      if (tripData.participants.length >= 8) {
        setError('This trip is full (max 8 passengers).');
        setLoading(false);
        return;
      }

      const userName = user.displayName || user.email.split('@')[0];
      const newParticipant = {
        uid: user.uid,
        name: userName,
        email: user.email,
        isHost: false,
        score: 0,
        joinedAt: new Date().toISOString(),
      };

      await updateDoc(doc(db, 'roadTrips', tripDoc.id), {
        participants: arrayUnion(newParticipant),
        participantIds: arrayUnion(user.uid),
        updatedAt: serverTimestamp(),
      });

      setActiveTrip({
        id: tripDoc.id,
        ...tripData,
        participants: [...tripData.participants, newParticipant],
      });
      setView('active');
    } catch (err) {
      console.error('Error joining trip:', err);
      console.error('Error details:', {
        code: err.code,
        message: err.message,
        stack: err.stack,
      });
      setError(
        `Failed to join trip: ${
          err.message || err.code || 'Unknown error'
        }. Please check the browser console for details.`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveTrip = () => {
    setActiveTrip(null);
    setView('home');
    setTripName('');
    setTripPurpose('');
    setJoinCode('');
  };

  if (view === 'active' && activeTrip) {
    return (
      <TripSession trip={activeTrip} user={user} onLeave={handleLeaveTrip} />
    );
  }

  return (
    <div className="road-trip-page">
      <div className="road-trip-container">
        <div className="road-trip-header">
          <div className="road-trip-logo">🚗</div>
          <h1>Road Trip Mode</h1>
          <p className="road-trip-tagline">
            Collaborative playlists & games for the journey
          </p>
        </div>

        {error && (
          <div className="road-trip-error">
            <span>⚠️</span> {error}
          </div>
        )}

        {view === 'home' && (
          <div className="road-trip-home">
            <div className="road-trip-options">
              <button
                className="road-trip-option-btn create"
                onClick={() => setView('create')}
              >
                <span className="option-icon">🎉</span>
                <span className="option-title">Start a Trip</span>
                <span className="option-desc">
                  Create and invite passengers
                </span>
              </button>

              <button
                className="road-trip-option-btn join"
                onClick={() => setView('join')}
              >
                <span className="option-icon">🎫</span>
                <span className="option-title">Join a Trip</span>
                <span className="option-desc">Enter a code to join</span>
              </button>
            </div>

            {myTrips.length > 0 && (
              <div className="road-trip-existing">
                <h3>Your Active Trips</h3>
                {myTrips.map((trip) => (
                  <button
                    key={trip.id}
                    className="existing-trip-btn"
                    onClick={() => {
                      setActiveTrip(trip);
                      setView('active');
                    }}
                  >
                    <span className="trip-emoji">🚗</span>
                    <div className="trip-info">
                      <span className="trip-name">{trip.name}</span>
                      <span className="trip-meta">
                        {trip.participants?.length || 1} passengers •{' '}
                        {trip.code}
                      </span>
                    </div>
                    <span className="trip-arrow">→</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {view === 'create' && (
          <div className="road-trip-form">
            <button className="back-btn" onClick={() => setView('home')}>
              ← Back
            </button>

            <h2>🎉 Start a Road Trip</h2>

            <div className="form-group">
              <label>Trip Name *</label>
              <input
                type="text"
                placeholder="e.g., Vegas Road Trip 2025"
                value={tripName}
                onChange={(e) => setTripName(e.target.value)}
                maxLength={50}
              />
            </div>

            <div className="form-group">
              <label>Trip Purpose (optional)</label>
              <input
                type="text"
                placeholder="e.g., Dad's 60th birthday"
                value={tripPurpose}
                onChange={(e) => setTripPurpose(e.target.value)}
                maxLength={100}
              />
            </div>

            <div className="form-group">
              <label className="toggle-label">
                <span>🎮 Enable Games</span>
                <span className="toggle-hint">"Who Requested This?" game before each song</span>
              </label>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="gamesToggle"
                  checked={gamesEnabled}
                  onChange={(e) => setGamesEnabled(e.target.checked)}
                />
                <label htmlFor="gamesToggle" className="toggle-slider">
                  <span className="toggle-text">{gamesEnabled ? 'ON' : 'OFF'}</span>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>Invite Participants (optional)</label>
              <button
                type="button"
                className="participant-selector-btn"
                onClick={() =>
                  setShowParticipantSelector(!showParticipantSelector)
                }
              >
                {selectedParticipants.length > 0
                  ? `👥 ${selectedParticipants.length} selected`
                  : '👥 Select Participants'}
              </button>

              {showParticipantSelector && (
                <div className="participant-selector">
                  {availableUsers.length > 0 ? (
                    <>
                      <div className="participant-list">
                        {availableUsers.map((u) => {
                          const isSelected = selectedParticipants.some(
                            (p) => p.uid === u.uid
                          );
                          return (
                            <label key={u.uid} className="participant-item">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedParticipants([
                                      ...selectedParticipants,
                                      u,
                                    ]);
                                  } else {
                                    setSelectedParticipants(
                                      selectedParticipants.filter(
                                        (p) => p.uid !== u.uid
                                      )
                                    );
                                  }
                                }}
                              />
                              <span className="participant-name">{u.name}</span>
                              <span className="participant-email">
                                {u.email}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      {selectedParticipants.length > 0 && (
                        <div className="selected-participants">
                          <strong>
                            Selected ({selectedParticipants.length}):
                          </strong>
                          {selectedParticipants.map((p) => (
                            <span key={p.uid} className="selected-tag">
                              {p.name} ✕
                              <button
                                type="button"
                                onClick={() =>
                                  setSelectedParticipants(
                                    selectedParticipants.filter(
                                      (sel) => sel.uid !== p.uid
                                    )
                                  )
                                }
                                className="remove-participant"
                              ></button>
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="no-users-message">
                      <p>No other users found in the system.</p>
                      <p>
                        You can still share the trip code manually after
                        creating the trip.
                      </p>
                    </div>
                  )}
                  <button
                    type="button"
                    className="close-selector-btn"
                    onClick={() => setShowParticipantSelector(false)}
                  >
                    Done
                  </button>
                </div>
              )}

              {selectedParticipants.length > 0 && (
                <p className="form-hint">
                  📧 Invitations will be sent to {selectedParticipants.length}{' '}
                  participant(s) via email
                </p>
              )}
            </div>

            <button
              className="submit-btn"
              onClick={handleCreateTrip}
              disabled={loading || !tripName.trim() || sendingInvites}
            >
              {sendingInvites
                ? '📧 Sending invitations...'
                : loading
                ? '🔄 Creating...'
                : '🚀 Create Trip'}
            </button>
          </div>
        )}

        {view === 'join' && (
          <div className="road-trip-form">
            <button className="back-btn" onClick={() => setView('home')}>
              ← Back
            </button>

            <h2>🎫 Join a Road Trip</h2>

            <div className="form-group">
              <label>Enter Trip Code</label>
              <input
                type="text"
                placeholder="ABC123"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                maxLength={6}
                className="code-input"
              />
              <p className="input-hint">
                Ask the host for the 6-character code
              </p>
            </div>

            <button
              className="submit-btn"
              onClick={handleJoinTrip}
              disabled={loading || joinCode.length !== 6}
            >
              {loading ? '🔄 Joining...' : '🎵 Join Trip'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default RoadTrip;
